import { ReactNode, useMemo } from 'react'

import { SearchConditionBase } from '@/common/api/search'
import { ListFollowProps } from '@/common/app/list'
import { multipleRectTotalWidth } from '@/common/util/rect'

import Scrollbar from '@/components/view/atoms/scrollbar/Scrollbar'
import Table from '@/components/view/atoms/table/Table'
import TableRow from '@/components/view/atoms/table/TableRow'
import Text from '@/components/view/atoms/text/Text'
import BottomFloat from '@/components/view/layouts/float/BottomFloat'
import TheadFloat from '@/components/view/layouts/float/TheadFloat'

import Pagination from '@/components/container/utils/pager/Pagination'

interface Props {
  listFollowProps: ListFollowProps
  tableHead: ReactNode
  tableFloatHead: ReactNode
  body: ReactNode
  condition: SearchConditionBase
  onUpdatePage: (page: number) => void
  total: number
  selectedIdsQuantity?: number
  selectedNode?: ReactNode
  additionTableHead?: ReactNode
}

const THEAD_FLOAT_TOP = 56

const ScrollFollowTable = ({
  listFollowProps,
  tableHead,
  tableFloatHead,
  body,
  condition,
  onUpdatePage,
  total,
  selectedIdsQuantity,
  selectedNode,
  additionTableHead
}: Props) => {
  const { floatButtonFixed, tableRect, barRef, onScrollBar, headerFloat, header, table } =
    listFollowProps
  const selectedQuantity = header.isScrolled ? 0 : selectedIdsQuantity

  const isScrollableX = useMemo(() => {
    return !!tableRect?.width && multipleRectTotalWidth(header.rowRects) > tableRect.width
  }, [tableRect, header.rowRects])

  return (
    <>
      {header.rowRects.length > 0 && (
        <TheadFloat
          ref={headerFloat.headFloatRef}
          top={THEAD_FLOAT_TOP}
          width={headerFloat.width}
          showing={headerFloat.isScrolledToTable}
          selectedQuantity={selectedQuantity}
        >
          {tableFloatHead}
        </TheadFloat>
      )}
      <Table
        onScroll={table.onScrollTable}
        ref={table.tableRef}
        selectedQuantity={selectedQuantity}
        height={
          selectedQuantity && selectedQuantity > 0 && header.rowRects.length > 0
            ? header.rowRects[0]?.height
            : undefined
        }
        bottomPadding={floatButtonFixed}
      >
        <thead>
          {additionTableHead && <TableRow head>{additionTableHead}</TableRow>}
          <TableRow head>{tableHead}</TableRow>
        </thead>
        <tbody>{body}</tbody>
      </Table>
      <BottomFloat
        isFixed={floatButtonFixed}
        width={tableRect?.width}
        border={!isScrollableX}
        scrollBar={
          isScrollableX ? (
            <Scrollbar
              ref={barRef}
              onScroll={onScrollBar}
              showing={floatButtonFixed || isScrollableX}
              allWidth={multipleRectTotalWidth(header.rowRects)}
            />
          ) : null
        }
        pager={
          <>
            <Pagination
              page={condition.page}
              perPage={condition.perPage}
              total={total}
              onPrev={onUpdatePage}
              onNext={onUpdatePage}
            />
          </>
        }
      >
        {selectedQuantity != undefined && selectedQuantity > 0 && selectedNode && (
          <>
            <Text>選択した{selectedQuantity}個を一括操作</Text>
            {selectedNode}
          </>
        )}
      </BottomFloat>
    </>
  )
}

export default ScrollFollowTable
